import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import useTenantApi from './config';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import { useAuth } from '../../context/AuthContext';

const Container = styled.div``;

export default function TenantsPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const { isSuperAdmin, accessibleTenants, activeTenantId } = useAuth();
  const activeTenant = accessibleTenants.find((tn) => tn.id === activeTenantId);
  // Holding admin can create only when their active tenant is a Holding —
  // subsidiary admins have nowhere to create under.
  const canCreate = isSuperAdmin || activeTenant?.tenantType === 1;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, ids: [], name: '' });
  const [deleting, setDeleting] = useState(false);

  const { objectSchema, fetching, columns: baseColumns, data, pagination, create, update, _delete } = useTenantApi(queryParams);

  const handleNewButtonClick = useCallback(() => {
    setSelectedRow(null);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setSelectedRow({
      ...row,
      expireDate: row.expireDate ? row.expireDate.substring(0, 10) : '',
      registrationDate: row.registrationDate ? row.registrationDate.substring(0, 10) : '',
    });
    setDrawerOpen(true);
  }, []);

  const handleSave = useCallback(async (formData) => {
    const success = selectedRow
      ? await update(selectedRow.id, formData)
      : await create(formData);
    if (success) setDrawerOpen(false);
    return success;
  }, [selectedRow, create, update]);

  const handleDelete = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, ids: [row.id], name: row.name });
  }, []);

  const handleRowsDelete = useCallback((ids) => {
    setDeleteConfirm({ isOpen: true, ids, name: `${ids.length} ${t('tenant.title').toLowerCase()}` });
  }, [t]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    await _delete(deleteConfirm.ids);
    setDeleteConfirm({ isOpen: false, ids: [], name: '' });
    setDeleting(false);
  }, [_delete, deleteConfirm.ids]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const handleRowClick = useCallback((row) => {
    history.push(`/tenants/${row.id}`);
  }, [history]);

  // Holding admins can edit + delete tenants in their accessible scope; backend
  // locks tenantType/parentTenantId/licensing on update and gates Delete via scope.
  const actionOptions = useMemo(() => [
    { type: 'details', label: t('tenant.details'), onClick: (row) => history.push(`/tenants/${row.id}`) },
    { type: 'edit', label: t('tenant.edit'), onClick: handleEdit },
    { type: 'divider' },
    { type: 'delete', label: t('tenant.delete'), onClick: handleDelete },
  ], [t, handleEdit, handleDelete, history]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'actions', label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <OptionsMenu actions={actionOptions} row={row} />;
        },
      },
    },
  ], [baseColumns, actionOptions, data]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedRow ? t('tenant.edit') : t('tenant.new')}
        schema={objectSchema}
        initialValues={selectedRow}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('tenant.deleteTitle')}
        message={t('tenant.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, ids: [], name: '' })}
        loading={deleting}
      />

      {useMemo(() => (
        <DataTable
          title={t('tenant.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={canCreate}
          onNewButtonClick={canCreate ? handleNewButtonClick : undefined}
          onRowsDelete={handleRowsDelete}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          onRowClick={handleRowClick}
        />
      ), [t, fetching, columns, data, pagination, handleNewButtonClick, handleRowsDelete, filterList, handleFilterConfirm, handleDataStateChange, handleRowClick, canCreate])}
    </Container>
  );
}
